import * as React from "react";
import { ChevronLeft, ChevronRight, ImageOff, Play } from "lucide-react";
import type { AssessmentMedia } from "@taskiq/shared";
import { Modal } from "@/components/ui/Modal";
import { VideoPlayer } from "@/components/ui/VideoPlayer";
import { cn } from "@/lib/cn";

/**
 * The evidence on a check card: fixed-size tiles here, everything full size in
 * a dialog.
 *
 * ── Why the card no longer holds a player ──────────────────────────────────
 *
 * A clip took the shape of whatever the technician recorded - 16:9 held, and a
 * phone held upright turned the frame 3:4, which on a half-width card is taller
 * than the findings beside it. One card of a four-module record then set the
 * height of the row and the reader scrolled past a sweep to reach the hazards
 * it found. Reviewing evidence and *scanning* a record are two jobs, and the
 * card is the second one.
 *
 * So every tile here is one known shape whatever is behind it, and the frame
 * the technician actually captured is one press away. That is the same trade
 * the phone already made - `mobile-app/.../media_viewer.dart` - and the two
 * clients now answer "show me the picture" the same way.
 *
 * ── Nothing is cropped, in either place ────────────────────────────────────
 *
 * The tile letterboxes rather than fills. A hazard at the edge of frame is the
 * one a reviewer opened this for, and a `cover` thumbnail is how it stops being
 * on the record. Letterboxing costs a band of sunken ground on a portrait
 * still, which is the cheaper of the two mistakes.
 *
 * ── Only a clip carries a glyph ────────────────────────────────────────────
 *
 * A play triangle says something a still frame does not: there is footage here
 * and pressing it starts. "This 120px picture gets bigger" is not news - it is
 * what a thumbnail is - and an expand mark on every photograph put a scrim over
 * the middle of the evidence to say so, five times on a PPE check.
 */

/** Cells in the thumbnail strip, counting the overflow tile as one of them. */
const STRIP_TILES = 4;

export interface ViewerItem {
  id: string;
  url: string;
  kind: "image" | "video";
  /** The still behind a clip, on the tile and in the dialog before play. */
  poster?: string | null;
  /** What this is, for the tile's label and the dialog's title. */
  label: string;
  /** A line under the frame in the dialog - which attempt, and when. */
  caption?: string;
}

/**
 * Everything submitted against one attempt, in the order the record reads it.
 *
 * The clip first, because on an area scan it is the evidence and the still is
 * its cover; photographs after, in the order they were taken. `inference_video`
 * wins over `raw_video` at the same attempt - same footage with the detector's
 * boxes drawn on, which is strictly more of the record.
 *
 * Deliberately the same reduction as the phone's `mediaForAttempt`, and it has
 * to stay that way: a supervisor moving between the two clients should meet one
 * record, not two orderings of it.
 */
export function viewerItems(media: AssessmentMedia, attempt?: number): ViewerItem[] {
  const items = media.items.filter(
    (item) => item.url !== null && (attempt === undefined || item.attempt === attempt)
  );

  const clips = items
    .filter((item) => item.kind === "raw_video" || item.kind === "inference_video")
    .sort((a, b) => a.seq - b.seq);
  const annotated = clips.filter((clip) => clip.kind === "inference_video");
  const sweep = annotated.length > 0 ? annotated : clips;

  const photos = items.filter((item) => item.kind === "raw_image").sort((a, b) => a.seq - b.seq);

  const poster =
    items.filter((item) => item.kind === "thumbnail").at(-1)?.url ?? media.thumbnail ?? null;

  const list: ViewerItem[] = [
    ...sweep.map((clip) => ({
      id: clip.id,
      url: clip.url!,
      kind: "video" as const,
      poster,
      label: "The recorded sweep",
    })),
    ...photos.map((photo, index) => ({
      id: photo.id,
      url: photo.url!,
      kind: "image" as const,
      label: photos.length === 1 ? "Photograph" : `Photograph ${index + 1}`,
    })),
  ];

  // A still with no clip and no photographs beside it is the evidence rather
  // than a cover for something else, so it earns a tile of its own.
  if (list.length === 0 && poster) {
    list.push({ id: "thumbnail", url: poster, kind: "image", label: "The captured frame" });
  }
  return list;
}

/**
 * One tile plus a strip of the rest, and a dialog behind all of them.
 *
 * `placeholder` is for a gallery holding a **column** open rather than
 * decorating a card: on a two-column check card, nothing here lets the detail
 * beside it reflow to the full width, which is a bigger lie about the record
 * than an empty frame saying "no photograph".
 *
 * ── The strip is one row, always ───────────────────────────────────────────
 *
 * It used to wrap, which on a PPE check with five photographs put three tiles
 * on one line and a lone fourth under it - a ragged second row that reads as a
 * layout accident rather than as "there is more". Past four, the last tile
 * becomes the count of what is behind it and opens the dialog on the first of
 * them. Nothing is dropped: every frame is in the dialog's own strip.
 */
export function MediaGallery({
  items,
  title,
  placeholder,
  compact,
  className,
}: {
  items: ViewerItem[];
  /**
   * The check this evidence belongs to - "PPE check", "Area scan" - and the
   * dialog's heading. Not the frame's own name: "Photograph 4 · 4 of 5" told a
   * reviewer nothing they could not see, and what they had lost track of by the
   * third dialog was which check they were looking at the evidence for.
   */
  title?: string;
  placeholder?: React.ReactNode;
  /**
   * No lead tile - every frame is a thumbnail in the strip.
   *
   * For a card where the photographs are supporting evidence rather than the
   * evidence. The ladder check is the case: what was measured is the angle, and
   * the dial is the reading; the photograph is what the angle was read *from*,
   * and giving it a 4:3 lead tile the same size as the area scan's clip said
   * the opposite - two instruments of equal weight stacked in one column, on a
   * card whose answer is a single number. It stays one press from full size.
   */
  compact?: boolean;
  className?: string;
}) {
  const [at, setAt] = React.useState<number | null>(null);
  const [primary, ...rest] = items;
  // Four cells, and the fourth is the overflow count when it has to be. Compact
  // has no lead tile, so all four cells are frames rather than three plus one.
  const strip = compact ? items : rest;
  const shown = strip.length > STRIP_TILES ? strip.slice(0, STRIP_TILES - 1) : strip;
  const hidden = strip.length - shown.length;
  const offset = compact ? 0 : 1;

  if (!primary) {
    if (!placeholder) return null;
    return (
      <div className={cn("mediatile mediatile--empty", compact && "mediatile--note", className)}>
        <ImageOff aria-hidden />
        <p>{placeholder}</p>
      </div>
    );
  }

  return (
    <div className={cn("gallery", compact && "gallery--compact", className)}>
      {!compact && <MediaTile item={primary} onOpen={() => setAt(0)} />}

      {strip.length > 0 && (
        <ul className="gallery__strip">
          {shown.map((item, index) => (
            <li key={item.id}>
              <MediaTile item={item} thumb onOpen={() => setAt(index + offset)} />
            </li>
          ))}
          {hidden > 0 && (
            <li>
              <button
                type="button"
                className="gallery__more"
                onClick={() => setAt(shown.length + offset)}
                aria-label={`Show the other ${hidden} frames`}
              >
                <span className="tnum">+{hidden}</span>
              </button>
            </li>
          )}
        </ul>
      )}

      {at !== null && (
        <MediaLightbox
          items={items}
          title={title}
          index={at}
          onIndex={setAt}
          onClose={() => setAt(null)}
        />
      )}
    </div>
  );
}

/**
 * One frame, at one of two fixed sizes.
 *
 * The glyph says which of the two things pressing it does - a clip plays, a
 * still opens - and it is on screen at rest rather than on hover, because a
 * reviewer on a touch screen has no hover and an affordance that needs a
 * pointer to be discovered may as well not be there.
 *
 * Exported for the area scan's key-frame strip, which is the same tile with a
 * timestamp under it rather than a gallery of its own.
 */
export function MediaTile({
  item,
  thumb,
  onOpen,
}: {
  item: ViewerItem;
  thumb?: boolean;
  onOpen: () => void;
}) {
  const [failed, setFailed] = React.useState(false);
  const still = item.kind === "video" ? item.poster : item.url;

  React.useEffect(() => setFailed(false), [still]);

  return (
    <button
      type="button"
      className={cn("mediatile", thumb && "mediatile--thumb")}
      onClick={onOpen}
      aria-label={`${item.label}. Opens full size.`}
    >
      {still && !failed && (
        <img
          src={still}
          alt=""
          loading="lazy"
          decoding="async"
          className="mediatile__image"
          onError={() => setFailed(true)}
        />
      )}
      {item.kind === "video" && (
        <span className="mediatile__glyph">
          <Play aria-hidden fill="currentColor" />
        </span>
      )}
    </button>
  );
}

/**
 * The frame the technician captured, at the size it was captured.
 *
 * One stage of a fixed height with both media letterboxed into it, so paging
 * from a portrait photograph to a landscape clip does not resize the dialog
 * under the reader's cursor.
 *
 * Autoplay is only for the item the reader opened *on*: pressing a play glyph
 * is a request to watch, and arrowing onto a clip is not. That is the whole of
 * why `openedAt` is kept.
 */
export function MediaLightbox({
  items,
  title,
  index,
  onIndex,
  onClose,
}: {
  items: ViewerItem[];
  /** The check the evidence belongs to. Falls back to the frame's own name. */
  title?: string;
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
}) {
  const [openedAt] = React.useState(index);
  const [failed, setFailed] = React.useState(false);
  const item = items[index];
  const many = items.length > 1;

  const step = React.useCallback(
    (by: number) => onIndex((index + by + items.length) % items.length),
    [index, items.length, onIndex]
  );

  React.useEffect(() => setFailed(false), [item?.url]);

  React.useEffect(() => {
    if (!many) return;
    function onKey(event: KeyboardEvent) {
      // The player owns the arrow keys while it has focus - they seek, and it
      // calls `preventDefault` to say so.
      if ((event.target as HTMLElement | null)?.closest(".player")) return;
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [many, step]);

  if (!item) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <>
          {title ?? item.label}
          {/* The position is orientation, not a name, so it is set back from
              the heading rather than joined to it with a separator. */}
          {many && (
            <span className="lightbox__position tnum">
              {index + 1} of {items.length}
            </span>
          )}
        </>
      }
      className="lightbox"
    >
      <div className="lightbox__stage">
        {item.kind === "video" ? (
          <VideoPlayer
            key={item.id}
            src={item.url}
            poster={item.poster}
            autoPlay={index === openedAt}
            label={item.label.toLowerCase()}
            className="lightbox__player"
          />
        ) : failed ? (
          <div className="lightbox__notice">
            <ImageOff aria-hidden />
            <p>
              That photograph could not be loaded. The upload may not have finished, or the link may
              have expired.
            </p>
          </div>
        ) : (
          <img
            src={item.url}
            alt={item.label}
            className="lightbox__image"
            onError={() => setFailed(true)}
          />
        )}

        {many && (
          <>
            <button
              type="button"
              className="lightbox__step lightbox__step--prev"
              onClick={() => step(-1)}
              aria-label="Previous"
            >
              <ChevronLeft aria-hidden />
            </button>
            <button
              type="button"
              className="lightbox__step lightbox__step--next"
              onClick={() => step(1)}
              aria-label="Next"
            >
              <ChevronRight aria-hidden />
            </button>
          </>
        )}
      </div>

      {item.caption && <p className="lightbox__caption">{item.caption}</p>}

      {/* The rest of the set, so a reviewer can see how many frames there are
          and jump to one rather than paging through the lot to find it. */}
      {many && (
        <ul className="lightbox__strip">
          {items.map((entry, at) => (
            <li key={entry.id}>
              <button
                type="button"
                className={cn("lightbox__pick", at === index && "lightbox__pick--on")}
                onClick={() => onIndex(at)}
                aria-label={entry.label}
                aria-current={at === index}
              >
                {(entry.kind === "video" ? entry.poster : entry.url) && (
                  <img
                    src={(entry.kind === "video" ? entry.poster : entry.url) ?? undefined}
                    alt=""
                    loading="lazy"
                    className="lightbox__pick-image"
                  />
                )}
                {entry.kind === "video" && (
                  <span className="lightbox__pick-glyph">
                    <Play aria-hidden fill="currentColor" />
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
